// ─── 🎾 I promoter (16/10/2026) ─────────────────────────────────────────────
//
// Mockup «Promoter», approvato: *«vai, tutte sì»*. 🔄 Lo stesso giorno il
// committente ci ha ripensato: *«leva il fatto che in alcuni orari … giorni non
// possono prenotare il campo. Lasciamo solamente la regola che non possono
// giocare due o più promoter insieme nella stessa partita senza pagare … se sono
// presenti due promoter nella stessa partita, pagano la quota normale, tutti
// quanti»*. Le regole:
//   · il promoter **non paga la quota campo**, a qualunque giorno e ora;
//   · con **due o più promoter** nella stessa partita, **pagano tutti** la quota
//     del listino; se ne resta uno solo, torna gratis;
//   · deve giocare **almeno 4 partite a settimana**: se non le gioca, va avvisato;
//   · riceve dei messaggi su **quanto ha risparmiato**;
//   · 📅 il **periodo** in cui è promoter, dal… al… (*«ci siamo scordati … il
//     periodo in cui il promoter è attivo, dal al»*): fuori dal periodo paga come
//     tutti. La regola guarda il giorno della partita, non il giorno in cui si
//     prenota. «Al» vuoto = fino a nuovo avviso;
//   · 🧑‍💼 la deroga **Staff**: *«persone dello staff che non pagano mai la quota
//     campo»*. Gratis sempre, non conta fra i promoter in campo, e niente minimo
//     né avvisi della settimana (non è un promoter con degli obblighi).
// E le idee approvate: l'avviso del giovedì a chi è indietro, il riepilogo della
// domenica, la pausa, il segnale alla Segreteria dopo 2 settimane sotto, l'omaggio
// in Cassa, e conta solo la partita non annullata e già giocata.
//
// ⚖️ La quota del promoter è **quella del listino** (risposta del committente):
// nessuna tariffa a parte. L'omaggio è proprio quella quota, non pagata.
import { z } from 'zod';
import { formatCents } from './money.js';
/**
 * 🎾 Il minimo di partite a settimana, **di ogni promoter** — 18/10/2026, mockup
 * «Minimo per promoter»: 🗣️ *«lo devo poter settare per ogni singolo promoter»*.
 * Non c'è più un numero per tutti: lo staff non ne ha.
 */
export const minimoPromoterSchema = z.number().int().min(1).max(14);
export const MINIMO_DA_SCRIVERE = 'Scrivi il minimo di partite a settimana, da 1 a 14.';
/**
 * ⭐ La quota di un promoter in una partita, dato quanti promoter ci sono in
 * campo (lui compreso).
 *
 * ⛔ Senza prezzo del listino non si inventa niente: `null` resta `null` (la
 * Segreteria lo vede), e l'omaggio non c'è.
 */
export function quotaDelPromoter(p) {
    if (!p.staff && p.promoterInCampo >= 2)
        return { amountDueCents: p.listinoCents, waivedCents: null, perche: 'altri_promoter' };
    if (p.listinoCents === null)
        return { amountDueCents: null, waivedCents: null, perche: 'gratis' };
    return { amountDueCents: 0, waivedCents: p.listinoCents, perche: 'gratis' };
}
/** Il giorno della settimana di una data `YYYY-MM-DD`: 1 = lunedì … 7 = domenica. */
export function giornoDellaSettimana(data) {
    const [a, m, g] = data.split('-').map(Number);
    const d = new Date(Date.UTC(a, m - 1, g)).getUTCDay();
    return d === 0 ? 7 : d;
}
// ── La settimana ─────────────────────────────────────────────────────────────
/** Il lunedì della settimana di una data (la settimana va dal lunedì alla domenica). */
export function lunediDi(data) {
    const [a, m, g] = data.split('-').map(Number);
    const d = new Date(Date.UTC(a, m - 1, g - (giornoDellaSettimana(data) - 1)));
    return d.toISOString().slice(0, 10);
}
export function piuGiorniDa(data, n) {
    const [a, m, g] = data.split('-').map(Number);
    return new Date(Date.UTC(a, m - 1, g + n)).toISOString().slice(0, 10);
}
/** 📅 Il giorno `data` sta nel periodo del promoter (estremi compresi)? */
export function nelPeriodo(periodo, data) {
    return periodo.dal <= data && (periodo.al === null || periodo.al >= data);
}
/** La pausa tocca quella settimana (anche un giorno solo)? Allora non si conta. */
export function settimanaInPausa(lunedi, pausa) {
    if (!pausa)
        return false;
    return pausa.dal <= piuGiorniDa(lunedi, 6) && pausa.al >= lunedi;
}
/** 📅 Fuori dal periodo: `da_iniziare` (il «dal» è più avanti) o `finito` (l'«al» è passato). */
export function fuoriPeriodo(periodo, oggi) {
    if (periodo.dal > oggi)
        return 'da_iniziare';
    if (periodo.al !== null && periodo.al < oggi)
        return 'finito';
    return null;
}
export function statoDellaSettimana(giocate, minimo, inPausa) {
    if (inPausa)
        return 'pausa';
    if (giocate >= minimo)
        return 'pieno';
    return giocate === 0 ? 'zero' : 'indietro';
}
/** Quante settimane chiuse di fila, dall'ultima, stanno sotto il minimo. */
export function settimaneDiFilaSotto(chiuse, minimo) {
    let n = 0;
    for (const s of chiuse) {
        if (s.inPausa || s.giocate === null)
            break;
        if (s.giocate >= minimo)
            break;
        n += 1;
    }
    return n;
}
/** Dopo quante settimane di fila sotto il minimo la Segreteria riceve il segnale. */
export const SETTIMANE_DEL_SEGNALE = 2;
// ── Le frasi per il promoter (testo semplice: il grassetto lo mette il bot) ──
const partite = (n) => (n === 1 ? '1 partita' : `${n} partite`);
const euro = (c) => formatCents(c).replace(/\u00a0/g, ' ');
/** 🎾 Giovedì alle 10, solo a chi è indietro. */
export function fraseMetaSettimana(nome, giocate, minimo) {
    const mancano = minimo - giocate;
    const prima = giocate === 0
        ? `🎾 Ciao ${nome}, questa settimana ti aspettano ancora ${partite(minimo)} per il regolamento dei promoter.`
        : `🎾 Ciao ${nome}, questa settimana hai giocato ${partite(giocate)}: ${mancano === 1 ? 'te ne manca 1' : `te ne mancano ${mancano}`} per il regolamento dei promoter.`;
    return `${prima}\nIn «📅 Prenota un campo» trovi i campi liberi: a qualunque ora per te sono gratis.`;
}
/**
 * 🎾 Domenica alle 20, a tutti i promoter non in pausa. ✂️ 19/10/2026 (collaudo, difetto 3):
 * 🗣️ *«se ha rispettato il numero minimo di partite giocate e quanto ha risparmiato. Niente di
 * più.»* Due righe, che vanno bene per soci e socie.
 */
export function fraseRiepilogo(p) {
    const mancano = p.minimo - p.giocate;
    const esito = mancano <= 0
        ? 'il minimo è rispettato'
        : `${mancano === 1 ? 'ne è mancata 1' : `ne sono mancate ${mancano}`} per il minimo`;
    return (`🎾 Ciao ${p.nome}, questa settimana hai giocato ${partite(p.giocate)} su ${p.minimo}: ${esito}.\n` +
        `Hai risparmiato ${euro(p.risparmioSettimanaCents)} di quota campo.`);
}
/** 📣 Alla Segreteria, in Segnalazioni: il gestionale non toglie niente da solo. */
export function fraseSegnalazionePromoter(nome, minimo, ultime) {
    const elenco = ultime.length === 1
        ? String(ultime[0])
        : `${ultime.slice(0, -1).join(', ')} e ${ultime[ultime.length - 1]}`;
    return `${nome} è sotto le ${minimo} partite da ${ultime.length} settimane (${elenco}). Il gestionale non toglie niente da solo: decidi tu dalla sua scheda.`;
}
// ── Il contratto con lo schermo ──────────────────────────────────────────────
export const pausaPromoterSchema = z
    .object({ dal: z.iso.date(), al: z.iso.date() })
    .refine((p) => p.dal <= p.al, { message: 'La pausa finisce dopo che comincia.' });
const STATI = ['pieno', 'indietro', 'zero', 'pausa', 'staff', 'da_iniziare', 'finito'];
export const promoterInElencoSchema = z.object({
    memberId: z.uuid(),
    nome: z.string(),
    staff: z.boolean(),
    dal: z.iso.date(),
    /** 📅 Fino a quando è promoter, compreso. `null` = fino a nuovo avviso. */
    al: z.iso.date().nullable(),
    pausa: z.object({ dal: z.iso.date(), al: z.iso.date() }).nullable(),
    /** Le partite a settimana che deve garantire. `null` per lo staff che non l'ha. */
    minimo: minimoPromoterSchema.nullable(),
    /** Partite già giocate questa settimana. */
    giocate: z.number().int(),
    /** `staff`: senza minimo, non si conta. `da_iniziare` / `finito`: fuori dal periodo. */
    stato: z.enum(STATI),
    /** Settimane chiuse di fila sotto il minimo (prima di questa). */
    settimaneSotto: z.number().int(),
    risparmioMeseCents: z.number().int(),
    risparmioTotaleCents: z.number().int(),
});
export const paginaPromoterSchema = z.object({
    /** La settimana in corso, dal lunedì alla domenica. */
    settimana: z.object({ dal: z.iso.date(), al: z.iso.date() }),
    items: z.array(promoterInElencoSchema),
});
/** 📅 Il periodo, dal… al… (compresi). `al` nullo = fino a nuovo avviso. */
export const periodoPromoterSchema = z
    .object({ dal: z.iso.date(), al: z.iso.date().nullable() })
    .refine((p) => p.al === null || p.dal <= p.al, { message: 'Il periodo finisce dopo che comincia.' });
export const aggiungiPromoterSchema = z
    .object({
    memberId: z.uuid(),
    staff: z.boolean().default(false),
    dal: z.iso.date(),
    al: z.iso.date().nullable().default(null),
    /** Obbligatorio per un promoter; lo staff può non averlo. */
    minimo: minimoPromoterSchema.nullable().default(null),
})
    .refine((p) => p.al === null || p.dal <= p.al, { message: 'Il periodo finisce dopo che comincia.' })
    .refine((p) => p.staff || p.minimo !== null, { message: MINIMO_DA_SCRIVERE, path: ['minimo'] });
/**
 * La spunta «Staff» di un promoter. Togliendola a chi un minimo non l'ha, il
 * minimo va scritto insieme (altrimenti la rotta risponde 400).
 */
export const scriviStaffSchema = z.object({ staff: z.boolean(), minimo: minimoPromoterSchema.optional() });
/** «Cambia» nella colonna «Minimo». */
export const scriviMinimoSchema = z.object({ minimo: minimoPromoterSchema });
export const promoterDelSocioSchema = z.object({
    promoter: z.boolean(),
    staff: z.boolean(),
    dal: z.iso.date().nullable(),
    al: z.iso.date().nullable(),
    pausa: z.object({ dal: z.iso.date(), al: z.iso.date() }).nullable(),
    /** `null` per chi non è promoter, e per lo staff che non l'ha. */
    minimo: z.number().int().nullable(),
    giocate: z.number().int(),
    stato: z.enum(STATI).nullable(),
    risparmioMeseCents: z.number().int(),
    risparmioTotaleCents: z.number().int(),
    /** Le ultime settimane chiuse, dalla più recente: partite giocate, `null` se in pausa. */
    ultime: z.array(z.number().int().nullable()),
});
/** Il corpo di «Metti in pausa» / «Togli la pausa». */
export const scriviPausaSchema = z.object({ pausa: pausaPromoterSchema.nullable() });
/**
 * 🤖 Il ponte: prima di prenotare o di entrare, il bot chiede quanto paga il
 * socio a quell'ora. Per chi non è promoter `promoter: false` e basta.
 */
export const domandaPromoterSchema = z.object({
    /** La partita in cui vuole entrare: senza, si risponde per una partita nuova. */
    bookingId: z.uuid().optional(),
});
export const promoterPerIlBotSchema = z.object({
    promoter: z.boolean(),
    /** In quella partita (o in una nuova) gioca gratis? Falso per chi non è promoter. */
    free: z.boolean(),
    reason: z.enum(['gratis', 'altri_promoter']).nullable(),
    /** La frase da dire prima del sì, già scritta. `null` quando gioca gratis o non è promoter. */
    text: z.string().nullable(),
    minimum: z.number().int(),
    playedThisWeek: z.number().int(),
    savedTotalCents: z.number().int(),
});
/**
 * La frase del bot prima di entrare in una partita dove c'è già un altro
 * promoter. Nessuna frase quando gioca gratis.
 */
export function fraseQuotaDelPromoter(perche, quotaCents) {
    if (perche === 'gratis')
        return null;
    const quota = quotaCents === null ? 'la quota del listino' : `la quota campo, ${euro(quotaCents)} a testa`;
    return `🎾 In questa partita c’è già un altro promoter: con due promoter in campo, giocate tutti con ${quota}.\nPer giocare gratis, in «🎾 Partite» trovi le altre partite.`;
}
//# sourceMappingURL=promoter.js.map